import RoundTab from "components/RoundTabButtons/RoundTab";
import moment from "moment-timezone";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Calendar, Views, momentLocalizer } from "react-big-calendar";
import DatePicker from "react-datepicker";
import { DndProvider, useDrag, useDragLayer, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { ColoredButton, PrimaryBgButton, PrimaryNewButton, PrimaryWhiteBgButton, WhiteButton } from "styledComponents/buttons";
import {
  CalendarButtonText,
  CalendarContainer,
  CalendarIcon,
  CalendarIconButton,
  CalendarLegend,
  CalendarToolbarContainer,
  DateCellWrapper,
  LegendDot,
  LegendItem,
  LegendSquare,
  MonthChangeIcon,
  MonthPickerContainer,
  MonthPickerInput,
  NextPrevButton,
  TimeSlotCellWrapper,
  TimeSlotWrapper,
  UnscheduledCollapsedTab,
  UnscheduledCollapsedText,
  UnscheduledCountBadge,
  UnscheduledToggleIcon,
  ViewButton,
  ViewButtonContainer,
} from "styledComponents/shiftAndSchedule";

import "../../assets/css/shift-schedule.css";
import avatar5 from "../../assets/img/avatar_5.svg";
import LeftArrow from "../../assets/img/icons/arrow_left_gray.svg";
import RightArrow from "../../assets/img/icons/arrow_right_gray.svg";
import calendarIcon from "../../assets/img/icons/calender.svg";
import noAssigneeIcon from "../../assets/img/icons/no-assignee.svg";
import useWindowDimensions from "../../components/CustomHooks/GetWindowDimentions";
import ComponentCircleLoader from "../../components/Loaders/ComponentCircleLoader";
import PricingPlan from "../../components/PricingPlan";
import { createNotification } from "../../modules/notificationManager";
import {
  CommonFlex,
  CommonGrid,
  CommonText,
  Container,
  PageTitle,
} from "../../styledComponents/common";
import {
  JobItemContainer,
  JobItemDot,
  JobItemMeta,
  JobItemTitle,
  ScheduleContainer,
  ScheduleRightContainer,
  ScheduleRightContainerBody,
  ScheduleRightContainerHeader,
} from "../../styledComponents/timesheet";
import { getHours, toHHMMSS } from "../../utils/helper";
import EditJobPopup from "../Jobs/EditJobPopup/EditJobPopupContainer";
import TimesheetFilter from "../Timesheet/TimesheetFilter";
import {
  DRAG_TYPES,
  DateCellWrapperComponent,
  DndBodyClassOnDrag,
  ShiftScheduleLegend,
  ShiftScheduleToolbar,
  TimeColumnWrapperComponent,
  TimeSlotCellWrapperComponent,
  TimeSlotWrapperComponent,
  UnscheduledDropZone,
  WeeklyGridEventCard,
} from "./Components/ShiftAndSchedulingComponent";
import ShowMoreRightSidePanel from "./Components/ShowMoreRightSidePanel";
import CreateOptionPopup from "./CreateOptionPopup";
import CreateShift from "./CreateShift/createShiftContainer";
import DailyShift from "./DailyShift";
import DeleteShiftPopup from "./DeleteShiftPopup";
import EditShift from "./EditShift/editShiftContainer";
import ShiftDetailsPopup from "./ShiftDetailsPopup";
import AddJobTimeRangeModal from "./AddJobTimeRangeModal";
import {
  createReschedulePayload as createReschedulePayloadHelper,
  createSchedulePayload as createSchedulePayloadHelper,
  createTimeSlotPayload as createTimeSlotPayloadHelper,
  createUnscheduledPayload as createUnscheduledPayloadHelper,
  determineType as determineTypeHelper,
  findJobFromUnscheduledList as findJobFromUnscheduledListHelper,
  findObjectById as findObjectByIdHelper,
  parseDateTimeInTimezone,
  validateFutureDate as validateFutureDateHelper,
} from "./shiftAndScheduleHelper";
import TrialEndBar from "custom_modules/Subscription/TrialEndBar";
import TrialEndingBanner from "components/TrialEndingBanner";
import AppOnboardingIndividualPage from "../../components/AppOnboarding/AppOnboardingIndividualPage";
import { selectOrganization } from "custom_modules/Organization/OrganizationActions";

// Week start day is managed globally by WeekConfigProvider (utils/weekConfig.jsx)
// which reads from selectedOrganization.configuration.start_day_of_week.
// Do NOT override moment.updateLocale here.
const localizer = momentLocalizer(moment);

// Drag and Drop Types
// DRAG_TYPES, TimeColumnWrapperComponent, TimeSlotCellWrapperComponent, TimeSlotWrapperComponent,
// DateCellWrapperComponent, UnscheduledDropZone, and isShiftScheduleDndDebugEnabled
// have been moved to ./Components/ShiftAndSchedulingComponent.jsx
const getTimes = date => {
  let startDate = moment(date).startOf("month");
  const endDate = moment(date).endOf("month");
  const times = [];
  while (
    startDate.isSameOrBefore(endDate) &&
    startDate.isSameOrBefore(moment())
  ) {
    times.push({
      title: "0h 00m",
      start: startDate.toDate(),
      end: startDate.toDate(),
      allDay: true,
    });
    startDate = moment(startDate).add(1, "day");
  }
  return times;
};

const CustomMonthPickerInput = ({ value, onClick }) => (
  <MonthPickerInput onClick={onClick}>{value}</MonthPickerInput>
);

// Toolbar extracted to Components/ShiftAndSchedulingComponent.jsx (keep main return here)

// parseDateTimeInTimezone moved to ./helper.js

const JobItem = ({ job }) => {
  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPES.JOB_ITEM,
    item: { jobId: job.id },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const meta =
    job?.work_order?.name ||
    job?.work_order?.customer_name ||
    job?.customer?.name ||
    job?.customer_name ||
    job?.address ||
    "";

  return (
    <JobItemContainer ref={drag} isDragging={isDragging} compact>
      <JobItemDot />
      <JobItemTitle style={{ fontSize: "11px", fontWeight: 600 }}>
        {job.name}
      </JobItemTitle>
    </JobItemContainer>
  );
};

// Event *content* renderer (week/day has a separate `.rbc-event-label` for time range)
// Keep this renderer presentation-only; drag/drop is handled by wrappers.
const DraggableEvent = ({ event, title, view, timeZone }) => {
  const isJob = Boolean(event?.work_order?.id);
  const isOffice = Boolean(event?.office);
  const accent = isOffice ? "#6C2EF2" : isJob ? "#2266EF" : "#2266EF";

  // Job schedules and office schedules represent assignees differently.
  // Only show the icon when we can confidently determine there is no assignee.
  const hasNoAssignee = isJob
    ? !(
      event?.assignee_id ||
      event?.assignee?.id ||
      event?.assignee ||
      event?.assigned_to_id ||
      event?.assigned_to?.id ||
      event?.assigned_to ||
      event?.user_id ||
      event?.user?.id ||
      (Array.isArray(event?.assignees) && event.assignees.length > 0)
    )
    : isOffice
      ? !(Array.isArray(event?.assignees) && event.assignees.length > 0)
      : false;

  const timeText =
    view === "month"
      ? parseDateTimeInTimezone(event?.start_time, timeZone)?.format(
        "h:mm a",
      ) || ""
      : "";

  const nameText = event?.name || event?.title || title || "";

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "4px",
        padding: "4px",
        boxSizing: "border-box",
        marginTop: "4px",
      }}
    >
      {/* Top row: dot + time (Figma: gap 4, align center) */}
      {view === "month" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            alignSelf: "stretch",
          }}
        >
          <span
            style={{
              width: "8px",
              height: "8px",
              borderRadius: isOffice ? "2px" : "50%", // Square for shifts, circle for jobs
              background: accent,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: "11px",
              fontWeight: 700,
              lineHeight: "17px",
              color: "#1f2939",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {timeText}
          </span>
          {hasNoAssignee && (
            <img
              src={noAssigneeIcon}
              alt="No assignee"
              style={{
                width: "12px",
                height: "12px",
                flexShrink: 0,
                marginLeft: "auto",
              }}
              title="No assignee"
            />
          )}
        </div>
      )}

      {/* Second row: name (truncate) */}
      <div
        style={{
          fontSize: "10px",
          fontWeight: 500,
          lineHeight: "14px",
          color: "#353A46",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          fontSize: "12px",
        }}
        title={nameText}
      >
        <span
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            flex: 1,
          }}
        >
          {nameText}
        </span>
        {hasNoAssignee && view !== "month" && (
          <img
            src={noAssigneeIcon}
            alt="No assignee"
            style={{
              width: "12px",
              height: "12px",
              flexShrink: 0,
            }}
            title="No assignee"
          />
        )}
      </div>
    </div>
  );
};

// Wrap the whole `.rbc-event` (label + content) so the *entire* block is draggable.
const DraggableEventWrapper = ({
  event,
  children,
  selectedOrganization,
  timeZone,
}) => {
  const didDragRef = useRef(false);

  // Check if event has already started
  const isEventPast = React.useMemo(() => {
    if (!event?.start_time) return false;
    const orgTimezone =
      selectedOrganization?.timezone || timeZone || moment.tz.guess();
    const eventStart = parseDateTimeInTimezone(event.start_time, orgTimezone);
    if (!eventStart) return false;
    const now = moment.tz(orgTimezone);
    return eventStart.isBefore(now);
  }, [event?.start_time, selectedOrganization?.timezone, timeZone]);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DRAG_TYPES.CALENDAR_EVENT,
      item: { eventId: event?.id },
      canDrag: () => {
        return Boolean(event?.id);
      },
      collect: monitor => {
        const dragging = monitor.isDragging();
        if (dragging) didDragRef.current = true;
        return { isDragging: dragging };
      },
      end: () => {
        // Allow the click event (if any) to flush first, then reset.
        setTimeout(() => {
          didDragRef.current = false;
        }, 0);
      },
    }),
    [event?.id, isEventPast],
  );

  if (!React.isValidElement(children)) return children;

  const existingOnClick = children.props?.onClick;
  const existingOnMouseDown = children.props?.onMouseDown;

  return React.cloneElement(children, {
    ref: node => drag(node),
    style: {
      ...(children.props?.style || {}),
      opacity: isDragging ? 0.5 : children.props?.style?.opacity,
      cursor: isDragging ? "grabbing" : "grab",
    },
    onMouseDown: e => {
      // Prevent react-big-calendar from swallowing the gesture so drag can start reliably.
      e.stopPropagation();
      didDragRef.current = false;
      if (typeof existingOnMouseDown === "function") existingOnMouseDown(e);
    },
    onTouchStart: e => {
      e.stopPropagation();
    },
    onClick: e => {
      // Prevent "click to open details" after completing a drag.
      if (didDragRef.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (typeof existingOnClick === "function") existingOnClick(e);
    },
  });
};

// Daily (Figma) grid: times across top, single day row (no date column on left).
// Uses the SAME drop handler as RBC time-grid cells, so DnD stays intact.
const DailyGridView = ({
  selectedDate,
  selectedOrganization,
  calendarStep = 60,
  events,
  // DnD / scheduling props forwarded into TimeSlotCellWrapperComponent
  unscheduledJobsList,
  findJobFromUnscheduledList,
  findObjectById,
  validateFutureDate,
  createTimeSlotPayload,
  determineType,
  updateJobRequest,
  editShift,
  officeShiftsList,
  jobShiftsList,
  setShiftsList,
  setUnscheduledJobsList,
  setIsEdited,
  trackOptimisticUpdate,
  onSelectEvent,
  onUnscheduledJobDropped,
}) => {
  const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();
  const day = moment.tz(selectedDate, orgTimezone).startOf("day");

  // No day label column for daily view
  const HOUR_COL_PX = 93.2;

  // Figma shows hourly columns; keep it simple + aligned with design.
  const hours = Array.from({ length: 24 }, (_, h) => h);
  const gridTemplateColumns = `repeat(${hours.length}, ${HOUR_COL_PX}px)`;
  const overlayGridTemplateColumns = `repeat(${hours.length}, ${HOUR_COL_PX}px)`;

  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const [overflowState, setOverflowState] = useState({
    hasOverflow: false,
    thumbLeft: 0,
    thumbWidth: 120,
  });
  // Ensure the row grid fills the available scroll viewport height
  const [minBodyHeight, setMinBodyHeight] = useState(0);

  const recalcOverflow = useCallback(() => {
    const el = scrollRef.current;
    const trackEl = trackRef.current;
    if (!el) return;

    // Update the minimum body height so the row grid stretches to fill
    // the available viewport (minus the header height of 40px).
    const HEADER_HEIGHT = 40; // matches .schedule-weekly-grid__time-header
    const viewportHeight = el.clientHeight;
    const availableBodyHeight = Math.max(0, viewportHeight - HEADER_HEIGHT);
    setMinBodyHeight(availableBodyHeight);

    // Horizontal overflow / thumb calculations (only if the overflow track exists)
    const scrollWidth = el.scrollWidth;
    const clientWidth = el.clientWidth;
    const scrollLeft = el.scrollLeft;
    const hasOverflow = scrollWidth > clientWidth + 1;

    if (!hasOverflow || !trackEl) {
      setOverflowState({ hasOverflow: false, thumbLeft: 0, thumbWidth: 120 });
      return;
    }

    const trackWidth = trackEl.clientWidth;
    const minThumb = 60;
    const thumbWidth = Math.max(
      minThumb,
      Math.round((clientWidth / scrollWidth) * trackWidth),
    );
    const maxScrollLeft = Math.max(1, scrollWidth - clientWidth);
    const maxThumbLeft = Math.max(0, trackWidth - thumbWidth);
    const thumbLeft = Math.round((scrollLeft / maxScrollLeft) * maxThumbLeft);

    setOverflowState({ hasOverflow: true, thumbLeft, thumbWidth });
  }, []);

  const dayEventLanes = useMemo(() => {
    const dayKey = day.format("YYYY-MM-DD");
    const dayStart = moment.tz(dayKey, orgTimezone).startOf("day");
    const dayEnd = moment(dayStart).add(1, "day");

    const items = [];
    (Array.isArray(events) ? events : []).forEach(ev => {
      const start = ev?.start_time
        ? parseDateTimeInTimezone(ev.start_time, orgTimezone)
        : null;
      const end = ev?.end_time
        ? parseDateTimeInTimezone(ev.end_time, orgTimezone)
        : start
          ? moment(start).add(calendarStep, "minutes")
          : null;
      if (!start || !end) return;

      if (start.isBefore(dayEnd) && end.isAfter(dayStart)) {
        items.push({ ev, start, end, dayStart, dayEnd });
      }
    });

    const spans = items
      .map(({ ev, start, end }) => {
        const s = moment.max(start, dayStart);
        const e = moment.min(end, dayEnd);

        const startMinutes = s.diff(dayStart, "minutes", true);
        const endMinutes = e.diff(dayStart, "minutes", true);
        const startH = startMinutes / 60;
        const endH = endMinutes / 60;

        const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

        const startCol = clamp(Math.floor(startH), 0, 23);
        const isExactHour = Number.isInteger(endH);
        let endCol = isExactHour ? Math.round(endH) : Math.floor(endH) + 1;
        endCol = clamp(endCol, startCol + 1, 24);

        const startOffset = (startH - Math.floor(startH)) * 100;
        const endOffset = isExactHour ? 100 : (endH - Math.floor(endH)) * 100;

        return { ev, s, e, startCol, endCol, startOffset, endOffset };
      })
      .sort((a, b) => {
        const aTs = a.s.valueOf();
        const bTs = b.s.valueOf();
        if (aTs !== bTs) return aTs - bTs;
        return (a.ev?.id ?? 0) > (b.ev?.id ?? 0) ? 1 : -1;
      });

    const lanes = [];
    spans.forEach(span => {
      let placed = false;
      for (let i = 0; i < lanes.length; i++) {
        const lane = lanes[i];
        const last = lane[lane.length - 1];
        if (span.s.isSameOrAfter(last.e)) {
          lane.push(span);
          placed = true;
          break;
        }
      }
      if (!placed) lanes.push([span]);
    });

    return lanes;
  }, [events, orgTimezone, day, calendarStep]);

  useEffect(() => {
    recalcOverflow();
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => recalcOverflow();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", recalcOverflow);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", recalcOverflow);
    };
  }, [recalcOverflow]);

  const dayKey = day.format("YYYY-MM-DD");
  const lanes = dayEventLanes;
  const rowHeight = Math.max(
    minBodyHeight,
    Math.max(48, lanes.length > 0 ? lanes.length * 46 + 6 : 48),
  );

  return (
    <div className="schedule-weekly-grid">
      <div className="schedule-weekly-grid__scroll" ref={scrollRef}>
        <div
          className="schedule-weekly-grid__header"
          style={{ display: "grid", gridTemplateColumns }}
        >
          {hours.map(h => (
            <div key={h} className="schedule-weekly-grid__time-header">
              {moment().hour(h).minute(0).format("h:mm a")}
            </div>
          ))}
        </div>

        <div className="schedule-weekly-grid__body">
          <div
            className="schedule-weekly-grid__row-wrap"
            style={{ height: rowHeight }}
          >
            <div
              className="schedule-weekly-grid__row-grid"
              style={{
                display: "grid",
                gridTemplateColumns,
                height: "100%",
              }}
            >
              {hours.map(h => {
                const slotStart = moment
                  .tz(dayKey, orgTimezone)
                  .hour(h)
                  .minute(0)
                  .second(0)
                  .millisecond(0)
                  .toDate();
                const key = `${dayKey}|${h}`;
                return (
                  <TimeSlotCellWrapperComponent
                    key={key}
                    slotTime={slotStart}
                    calendarStep={calendarStep}
                    unscheduledJobsList={unscheduledJobsList}
                    selectedOrganization={selectedOrganization}
                    findJobFromUnscheduledList={findJobFromUnscheduledList}
                    findObjectById={findObjectById}
                    validateFutureDate={validateFutureDate}
                    createTimeSlotPayload={createTimeSlotPayload}
                    determineType={determineType}
                    updateJobRequest={updateJobRequest}
                    editShift={editShift}
                    officeShiftsList={officeShiftsList}
                    jobShiftsList={jobShiftsList}
                    setShiftsList={setShiftsList}
                    setUnscheduledJobsList={setUnscheduledJobsList}
                    setIsEdited={setIsEdited}
                    trackOptimisticUpdate={trackOptimisticUpdate}
                  >
                    <div className="schedule-weekly-grid__cell" />
                  </TimeSlotCellWrapperComponent>
                );
              })}
            </div>

            {lanes.length > 0 ? (
              <div
                className="schedule-weekly-grid__overlay"
                style={{
                  display: "grid",
                  gridTemplateColumns: overlayGridTemplateColumns,
                  gridTemplateRows: `repeat(${lanes.length}, 46px)`,
                  // No left offset for daily view (no day label column)
                  left: "0",
                }}
              >
                {lanes.flatMap((lane, laneIdx) =>
                  lane.map(span => {
                    const timeText = `${span.s.format(
                      "h:mm a",
                    )} - ${span.e.format("h:mm a")}`;

                    const leftPx =
                      ((span.startOffset || 0) / 100) * HOUR_COL_PX;
                    const endOffsetPct =
                      span.endOffset !== undefined ? span.endOffset : 100;
                    const rightPx = ((100 - endOffsetPct) / 100) * HOUR_COL_PX;

                    return (
                      <div
                        key={`${span.ev.id}-${laneIdx}`}
                        className="schedule-weekly-grid__overlay-item"
                        style={{
                          gridColumn: `${span.startCol + 1} / ${span.endCol + 1
                            }`,
                          gridRow: `${laneIdx + 1}`,
                          marginLeft: `${leftPx}px`,
                          marginRight: `${rightPx}px`,
                          boxSizing: "border-box",
                        }}
                      >
                        <WeeklyGridEventCard
                          event={span.ev}
                          onSelect={onSelectEvent}
                          timeText={timeText}
                          selectedOrganization={selectedOrganization}
                        />
                      </div>
                    );
                  }),
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {overflowState.hasOverflow ? (
        <div className="schedule-weekly-grid__overflow">
          <div
            ref={trackRef}
            className="schedule-weekly-grid__overflow-track"
            onMouseDown={e => {
              const trackEl = trackRef.current;
              const el = scrollRef.current;
              if (!trackEl || !el) return;
              const rect = trackEl.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const trackWidth = trackEl.clientWidth;
              const maxScrollLeft = Math.max(
                1,
                el.scrollWidth - el.clientWidth,
              );
              const maxThumbLeft = Math.max(
                0,
                trackWidth - overflowState.thumbWidth,
              );
              const nextThumbLeft = Math.max(
                0,
                Math.min(maxThumbLeft, x - overflowState.thumbWidth / 2),
              );
              const ratio = maxThumbLeft > 0 ? nextThumbLeft / maxThumbLeft : 0;
              el.scrollLeft = ratio * maxScrollLeft;
            }}
          >
            <div
              className="schedule-weekly-grid__overflow-thumb"
              style={{
                width: `${overflowState.thumbWidth}px`,
                left: `${overflowState.thumbLeft}px`,
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

// Weekly (Figma) grid: times across top, days down left (transposed vs RBC week view).
// Uses the SAME drop handler as RBC time-grid cells, so DnD stays intact.
const WeeklyGridView = ({
  selectedDate,
  selectedOrganization,
  calendarStep = 60,
  events,
  // DnD / scheduling props forwarded into TimeSlotCellWrapperComponent
  unscheduledJobsList,
  findJobFromUnscheduledList,
  findObjectById,
  validateFutureDate,
  createTimeSlotPayload,
  determineType,
  updateJobRequest,
  editShift,
  officeShiftsList,
  jobShiftsList,
  setShiftsList,
  setUnscheduledJobsList,
  setIsEdited,
  trackOptimisticUpdate,
  onSelectEvent,
}) => {
  const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();
  // Use startOf("week") so the week respects moment's locale dow,
  // which is set by WeekConfigProvider based on the org's start_day_of_week setting.
  const weekStart = moment.tz(selectedDate, orgTimezone).startOf("week");
  const days = Array.from({ length: 7 }, (_, i) =>
    moment(weekStart).add(i, "day"),
  );

  // Keep the weekly rows stretched to the available viewport height.
  // The weekly grid has a fixed visual baseline of ~740px (per design),
  // and we expand rows (by increasing their heights) so Mon–Sun always fill.
  const WEEKLY_GRID_MIN_HEIGHT = 740;
  const WEEKLY_HEADER_HEIGHT = 40; // matches .schedule-weekly-grid__time-header
  const WEEKLY_MIN_BODY_HEIGHT = WEEKLY_GRID_MIN_HEIGHT - WEEKLY_HEADER_HEIGHT;

  // Keep these in one place so the grid + overlay always match.
  const DAY_LABEL_COL_PX = 100;
  const HOUR_COL_PX = 93.2;

  // Figma shows hourly columns; keep it simple + aligned with design.
  const hours = Array.from({ length: 24 }, (_, h) => h);
  const gridTemplateColumns = `${DAY_LABEL_COL_PX}px repeat(${hours.length}, ${HOUR_COL_PX}px)`;
  // Overlay only needs hour columns (no day label), so events align correctly
  const overlayGridTemplateColumns = `repeat(${hours.length}, ${HOUR_COL_PX}px)`;

  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const [overflowState, setOverflowState] = useState({
    hasOverflow: false,
    thumbLeft: 0,
    thumbWidth: 120,
  });
  const [minBodyHeight, setMinBodyHeight] = useState(0);

  const recalcOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Keep row heights at least as tall as the available scroll viewport.
    setMinBodyHeight(Math.max(0, el.clientHeight - WEEKLY_HEADER_HEIGHT));

    const trackEl = trackRef.current;
    if (!trackEl) {
      setOverflowState({ hasOverflow: false, thumbLeft: 0, thumbWidth: 120 });
      return;
    }

    const scrollWidth = el.scrollWidth;
    const clientWidth = el.clientWidth;
    const scrollLeft = el.scrollLeft;
    const hasOverflow = scrollWidth > clientWidth + 1;

    if (!hasOverflow) {
      setOverflowState({ hasOverflow: false, thumbLeft: 0, thumbWidth: 120 });
      return;
    }

    const trackWidth = trackEl.clientWidth;
    const minThumb = 60;
    const thumbWidth = Math.max(
      minThumb,
      Math.round((clientWidth / scrollWidth) * trackWidth),
    );
    const maxScrollLeft = Math.max(1, scrollWidth - clientWidth);
    const maxThumbLeft = Math.max(0, trackWidth - thumbWidth);
    const thumbLeft = Math.round((scrollLeft / maxScrollLeft) * maxThumbLeft);

    setOverflowState({ hasOverflow: true, thumbLeft, thumbWidth });
  }, []);

  const dayEventLanes = useMemo(() => {
    // Build lanes per day so overlapping spans stack vertically instead of overlapping.
    const byDay = new Map();
    (Array.isArray(events) ? events : []).forEach(ev => {
      const start = ev?.start_time
        ? parseDateTimeInTimezone(ev.start_time, orgTimezone)
        : null;
      const end = ev?.end_time
        ? parseDateTimeInTimezone(ev.end_time, orgTimezone)
        : start
          ? moment(start).add(calendarStep, "minutes")
          : null;
      if (!start || !end) return;

      // Add to any day it overlaps
      days.forEach(day => {
        const dayStart = moment
          .tz(day.format("YYYY-MM-DD"), orgTimezone)
          .startOf("day");
        const dayEnd = moment(dayStart).add(1, "day");
        if (start.isBefore(dayEnd) && end.isAfter(dayStart)) {
          const dayKey = day.format("YYYY-MM-DD");
          const arr = byDay.get(dayKey) || [];
          arr.push({ ev, start, end, dayStart, dayEnd });
          byDay.set(dayKey, arr);
        }
      });
    });

    // For each day, clamp spans and lane-pack
    const result = new Map();
    byDay.forEach((items, dayKey) => {
      // Recalculate dayStart consistently for this day
      const dayStart = moment.tz(dayKey, orgTimezone).startOf("day");
      const dayEnd = moment(dayStart).add(1, "day");

      const spans = items
        .map(({ ev, start, end }) => {
          // Clamp event times to day boundaries
          const s = moment.max(start, dayStart);
          const e = moment.min(end, dayEnd);

          // Calculate hours as decimal from day start
          // Use diff with "minutes" and true flag for floating point precision
          const startMinutes = s.diff(dayStart, "minutes", true);
          const endMinutes = e.diff(dayStart, "minutes", true);
          const startH = startMinutes / 60;
          const endH = endMinutes / 60;

          // Grid is 24 hourly columns (0..23). Use an *exclusive* end column index (1..24).
          // This keeps placement consistent for exact-hour endings (e.g. 9:00 ends at the 9:00 boundary).
          const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

          const startCol = clamp(Math.floor(startH), 0, 23);
          const isExactHour = Number.isInteger(endH);
          let endCol = isExactHour ? Math.round(endH) : Math.floor(endH) + 1;
          endCol = clamp(endCol, startCol + 1, 24);

          // Offsets are within the FIRST/LAST hour column only.
          // IMPORTANT: for exact-hour endings we want to use 100% of the last included column.
          const startOffset = (startH - Math.floor(startH)) * 100;
          const endOffset = isExactHour ? 100 : (endH - Math.floor(endH)) * 100;

          return { ev, s, e, startCol, endCol, startOffset, endOffset };
        })
        .sort((a, b) => {
          const aTs = a.s.valueOf();
          const bTs = b.s.valueOf();
          if (aTs !== bTs) return aTs - bTs;
          return (a.ev?.id ?? 0) > (b.ev?.id ?? 0) ? 1 : -1;
        });

      const lanes = [];
      spans.forEach(span => {
        let placed = false;
        for (let i = 0; i < lanes.length; i++) {
          const lane = lanes[i];
          const last = lane[lane.length - 1];
          if (span.s.isSameOrAfter(last.e)) {
            lane.push(span);
            placed = true;
            break;
          }
        }
        if (!placed) lanes.push([span]);
      });

      result.set(dayKey, lanes);
    });

    return result;
  }, [events, orgTimezone, days, calendarStep]);

  const computedRowHeights = useMemo(() => {
    const baseHeights = days.map(day => {
      const dayKey = day.format("YYYY-MM-DD");
      const lanes = dayEventLanes.get(dayKey) || [];
      return Math.max(48, lanes.length > 0 ? lanes.length * 46 + 6 : 48);
    });

    const sumBase = baseHeights.reduce((acc, h) => acc + h, 0);
    const target = Math.max(WEEKLY_MIN_BODY_HEIGHT, minBodyHeight);

    if (sumBase >= target) return baseHeights;

    const extra = target - sumBase;
    const perRow = Math.floor(extra / baseHeights.length);
    const remainder = extra % baseHeights.length;

    return baseHeights.map((h, idx) => h + perRow + (idx < remainder ? 1 : 0));
  }, [days, dayEventLanes, minBodyHeight, WEEKLY_MIN_BODY_HEIGHT]);

  useEffect(() => {
    // Initial + keep in sync
    recalcOverflow();
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => recalcOverflow();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", recalcOverflow);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", recalcOverflow);
    };
  }, [recalcOverflow]);

  return (
    <div className="schedule-weekly-grid">
      <div className="schedule-weekly-grid__scroll" ref={scrollRef}>
        <div
          className="schedule-weekly-grid__header"
          style={{ display: "grid", gridTemplateColumns }}
        >
          <div className="schedule-weekly-grid__corner" />
          {hours.map(h => (
            <div key={h} className="schedule-weekly-grid__time-header">
              {moment().hour(h).minute(0).format("h:mm a")}
            </div>
          ))}
        </div>

        <div className="schedule-weekly-grid__body">
          {days.map((day, dayIdx) => {
            const dayKey = day.format("YYYY-MM-DD");
            const lanes = dayEventLanes.get(dayKey) || [];
            const rowHeight =
              computedRowHeights[dayIdx] ||
              Math.max(48, lanes.length > 0 ? lanes.length * 46 + 6 : 48);
            return (
              <div
                key={dayKey}
                className="schedule-weekly-grid__row-wrap"
                style={{ height: rowHeight }}
              >
                <div
                  className="schedule-weekly-grid__row-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns,
                    height: "100%",
                  }}
                >
                  <div className="schedule-weekly-grid__day-cell">
                    {day.format("ddd, DD MMM")}
                  </div>

                  {hours.map(h => {
                    const slotStart = moment
                      .tz(dayKey, orgTimezone)
                      .hour(h)
                      .minute(0)
                      .second(0)
                      .millisecond(0)
                      .toDate();
                    const key = `${dayKey}|${h}`;
                    return (
                      <TimeSlotCellWrapperComponent
                        key={key}
                        slotTime={slotStart}
                        calendarStep={calendarStep}
                        unscheduledJobsList={unscheduledJobsList}
                        selectedOrganization={selectedOrganization}
                        findJobFromUnscheduledList={findJobFromUnscheduledList}
                        findObjectById={findObjectById}
                        validateFutureDate={validateFutureDate}
                        createTimeSlotPayload={createTimeSlotPayload}
                        determineType={determineType}
                        updateJobRequest={updateJobRequest}
                        editShift={editShift}
                        officeShiftsList={officeShiftsList}
                        jobShiftsList={jobShiftsList}
                        setShiftsList={setShiftsList}
                        setUnscheduledJobsList={setUnscheduledJobsList}
                        setIsEdited={setIsEdited}
                        trackOptimisticUpdate={trackOptimisticUpdate}
                      >
                        <div className="schedule-weekly-grid__cell" />
                      </TimeSlotCellWrapperComponent>
                    );
                  })}
                </div>

                {lanes.length > 0 ? (
                  <div
                    className="schedule-weekly-grid__overlay"
                    style={{
                      display: "grid",
                      gridTemplateColumns: overlayGridTemplateColumns,
                      gridTemplateRows: `repeat(${lanes.length}, 46px)`,
                      // Position overlay to start after the day label column (100px)
                      left: `${DAY_LABEL_COL_PX}px`,
                    }}
                  >
                    {lanes.flatMap((lane, laneIdx) =>
                      lane.map(span => {
                        const timeText = `${span.s.format(
                          "h:mm a",
                        )} - ${span.e.format("h:mm a")}`;

                        // Place precisely within the spanned grid area.
                        // Use pixel offsets so the start/end fractions are applied to the FIRST/LAST column only.
                        const leftPx =
                          ((span.startOffset || 0) / 100) * HOUR_COL_PX;
                        const endOffsetPct =
                          span.endOffset !== undefined ? span.endOffset : 100;
                        const rightPx =
                          ((100 - endOffsetPct) / 100) * HOUR_COL_PX;

                        return (
                          <div
                            key={`${span.ev.id}-${laneIdx}`}
                            className="schedule-weekly-grid__overlay-item"
                            style={{
                              // Overlay grid only has hour columns (0-23), so use startCol/endCol directly
                              gridColumn: `${span.startCol + 1} / ${span.endCol + 1
                                }`,
                              gridRow: `${laneIdx + 1}`,
                              // Pixel offsets within the spanned area
                              marginLeft: `${leftPx}px`,
                              marginRight: `${rightPx}px`,
                              boxSizing: "border-box",
                            }}
                          >
                            <WeeklyGridEventCard
                              event={span.ev}
                              onSelect={onSelectEvent}
                              timeText={timeText}
                              selectedOrganization={selectedOrganization}
                            />
                          </div>
                        );
                      }),
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {overflowState.hasOverflow ? (
        <div className="schedule-weekly-grid__overflow">
          <div
            ref={trackRef}
            className="schedule-weekly-grid__overflow-track"
            onMouseDown={e => {
              const trackEl = trackRef.current;
              const el = scrollRef.current;
              if (!trackEl || !el) return;
              // Click-to-jump
              const rect = trackEl.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const trackWidth = trackEl.clientWidth;
              const maxScrollLeft = Math.max(
                1,
                el.scrollWidth - el.clientWidth,
              );
              const maxThumbLeft = Math.max(
                0,
                trackWidth - overflowState.thumbWidth,
              );
              const nextThumbLeft = Math.max(
                0,
                Math.min(maxThumbLeft, x - overflowState.thumbWidth / 2),
              );
              const ratio = maxThumbLeft > 0 ? nextThumbLeft / maxThumbLeft : 0;
              el.scrollLeft = ratio * maxScrollLeft;
            }}
          >
            <div
              className="schedule-weekly-grid__overflow-thumb"
              style={{
                width: `${overflowState.thumbWidth}px`,
                transform: `translateX(${overflowState.thumbLeft}px)`,
              }}
              onMouseDown={e => {
                e.stopPropagation();
                const el = scrollRef.current;
                const trackEl = trackRef.current;
                if (!el || !trackEl) return;
                const startX = e.clientX;
                const startScrollLeft = el.scrollLeft;
                const trackWidth = trackEl.clientWidth;
                const maxScrollLeft = Math.max(
                  1,
                  el.scrollWidth - el.clientWidth,
                );
                const maxThumbLeft = Math.max(
                  1,
                  trackWidth - overflowState.thumbWidth,
                );

                const onMove = ev => {
                  const dx = ev.clientX - startX;
                  const ratio = dx / maxThumbLeft;
                  el.scrollLeft = startScrollLeft + ratio * maxScrollLeft;
                };
                const onUp = () => {
                  window.removeEventListener("mousemove", onMove);
                  window.removeEventListener("mouseup", onUp);
                };
                window.addEventListener("mousemove", onMove);
                window.addEventListener("mouseup", onUp);
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

const ShiftSchedule = ({
  isLoading,
  isBackgroundRefreshing,
  selectedOrganization,
  officeShiftsList,
  jobShiftsList,
  deleteCallback,
  editCallback,
  editIsLoading,
  deleteIsLoading,
  createIsLoading,
  createdShiftInfo,
  history,
  deleteWorkOrderJob,
  workOrderJobDeleteIsLoading,
  deleteJobCallback,
  jobUpdate,
  getOfficeShiftsList,
  getJobShiftsList,
  deleteShifts,
  editShift,
  updateJobRequest,
  // for job container
  isLoadingJobs,
  totaljobsCount,
  pageSizeJob,
  jobList,
  reloadJobList,
  getJobList,
  // for assignee dropdown
  orgMembersShortList,
  memberShortListIsLoading,
  getOrganizationMembersShortList,
  // for app onboarding features
  appOnboardingFeatures,
  appOnboardingIsFeaturesLoading,
  getOnboardingFeatures,
  appOnboardingState,
  dismissOnboardingFeature,
  dismissFeatureLoading,
}) => {
  // Keep the Unscheduled column + the ShowMore panel in sync (right rail width).
  const UNSCHEDULED_EXPANDED_WIDTH = "240px";
  const UNSCHEDULED_COLLAPSED_WIDTH = "32px";

  const [selectedMember, setSelectedMember] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const [selectedShift, setSelectedShift] = useState(null);
  const [shiftsOnSelectedDate, setShiftsOnSelectedDate] = useState([]);
  const [selectedDateOfShowMore, setSelectedDateOfShowMore] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const [ShiftsList, setShiftsList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showMoreIsOpen, setShowMoreIsOpen] = useState(false);
  const [createIsOpen, setCreateIsOpen] = useState(false);
  const [createOptionIsOpen, setCreateOptionIsOpen] = useState(false);
  const [editIsOpen, setEditIsOpen] = useState(false);
  const [deleteIsOpen, setDeleteIsOpen] = useState(false);
  const [isCreated, setIsCreated] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [pricingPopup, setPricingPopup] = useState(false);
  const [pricingPopupText, setPricingPopupText] = useState("");

  const [view, setView] = useState(Views.MONTH);
  const { height, width } = useWindowDimensions();
  const plan = "free";
  const [editJobIsOpen, setEditJobIsOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [unscheduledJobsList, setUnscheduledJobsList] = useState([]);
  const [isUnscheduledPanelOpen, setIsUnscheduledPanelOpen] = useState(false);

  const [addTimeModalIsOpen, setAddTimeModalIsOpen] = useState(true);

  // Time range modal for unscheduled job drops
  const [isTimeRangeModalOpen, setIsTimeRangeModalOpen] = useState(false);
  const [selectedDateForTimeRange, setSelectedDateForTimeRange] = useState(null);
  const [selectedJobForTimeRange, setSelectedJobForTimeRange] = useState(null);

  // Track pending optimistic operations
  const [pendingOperations, setPendingOperations] = useState(new Map());
  // Track if an unschedule operation just completed (to skip auto-refresh)
  const [skipNextRefresh, setSkipNextRefresh] = useState(false);

  // Dynamic labels and paths based on organization type (default to "operation" if not defined)
  const orgType = selectedOrganization?.type || "operation";
  const isOperationType = orgType === "operation";
  const timeZone = selectedOrganization?.timezone || moment.tz.guess();
  const createItemPath = isOperationType
    ? "/user/create-task"
    : "/user/create-job";
  const createItemScheduleLabel = isOperationType
    ? "Schedule a Task"
    : "Schedule a Job";

  const role =selectedOrganization?.role;

  // Helper function to track optimistic updates
  const trackOptimisticUpdate = useCallback(
    (eventId, originalState, optimisticState, operationType, eventType) => {
      setPendingOperations(prev => {
        const next = new Map(prev);
        next.set(eventId, {
          eventId,
          originalState: { ...originalState },
          optimisticState: { ...optimisticState },
          operationType, // "reschedule", "schedule", "unschedule"
          eventType, // "job", "office"
        });
        return next;
      });
    },
    [],
  );

  // Helper: check if API item reflects the optimistic target
  const apiMatchesOptimistic = useCallback(
    (apiItem, pendingOp) => {
      if (!apiItem || !pendingOp || !pendingOp.optimisticState) return false;
      const opt = pendingOp.optimisticState;
      const aStart = parseDateTimeInTimezone(apiItem.start_time, timeZone);
      const aEnd = parseDateTimeInTimezone(apiItem.end_time, timeZone);
      const oStart = parseDateTimeInTimezone(opt.start_time, timeZone);
      const oEnd = parseDateTimeInTimezone(opt.end_time, timeZone);
      if (!aStart || !aEnd || !oStart || !oEnd) return false;
      // Consider match if times are equal down to minute and status aligns
      const timesMatch =
        aStart.valueOf() === oStart.valueOf() &&
        aEnd.valueOf() === oEnd.valueOf();
      const statusMatch = (apiItem.status || "") === (opt.status || "");
      return timesMatch && statusMatch;
    },
    [timeZone],
  );

  // Fetch onboarding features status
  useEffect(() => {
    if (selectedOrganization && selectedOrganization.id) {
      getOnboardingFeatures(selectedOrganization.id);
    }
  }, [selectedOrganization?.id, getOnboardingFeatures]);

  useEffect(() => {
    if (selectedOrganization && selectedOrganization.id) {
      // Only fetch office shifts for operation type organizations
      if (orgType !== "service") {
        getOfficeShiftsList({
          organization_id: selectedOrganization.id,
          start_date: moment(selectedDate).startOf("month").format("YYYY-MM-DD"),
          end_date: moment(selectedDate).endOf("month").format("YYYY-MM-DD"),
        });
      }
      getJobShiftsList({
        organization_id: selectedOrganization.id,
        start_date: moment(selectedDate).startOf("month").format("YYYY-MM-DD"),
        end_date: moment(selectedDate).endOf("month").format("YYYY-MM-DD"),
      });
    }
  }, [selectedOrganization, orgType]);

  // Fetch unscheduled jobs list
  const callGetJobsList = useCallback(() => {
    const payload = {
      status: "unscheduled",
      paginate: true,
      page: 1,
    };

    if (selectedOrganization && selectedOrganization.id) {
      payload.organization_id = selectedOrganization.id;
      getJobList(payload);
    }
  }, [selectedOrganization, getJobList]);

  useEffect(() => {
    if (selectedOrganization && selectedOrganization.id) {
      callGetJobsList();
    }
  }, [selectedOrganization, callGetJobsList]);

  // Populate unscheduledJobsList from jobList prop
  useEffect(() => {
    if (jobList && Array.isArray(jobList)) {
      setUnscheduledJobsList([...jobList]);
    } else {
      setUnscheduledJobsList([]);
    }
  }, [jobList, reloadJobList]);

  // Listen for custom event from BottomNavBar/QuickActionsPanel to open create shift modal
  useEffect(() => {
    const handleOpenCreateShift = () => {
      setCreateIsOpen(true);
    };
    window.addEventListener("OPEN_CREATE_SHIFT_MODAL", handleOpenCreateShift);
    return () => {
      window.removeEventListener("OPEN_CREATE_SHIFT_MODAL", handleOpenCreateShift);
    };
  }, []);

  useEffect(() => {
    if (
      createdShiftInfo &&
      !createIsLoading &&
      isCreated &&
      selectedOrganization &&
      selectedOrganization.id
    ) {
      onCreateChange(false);
      createToggle();
      let payload = {
        organization_id: selectedOrganization.id,
        start_date: moment(selectedDate).startOf("month").format("YYYY-MM-DD"),
        end_date: moment(selectedDate).endOf("month").format("YYYY-MM-DD"),
      };
      // Only fetch office shifts for operation type organizations
      if (orgType !== "service") {
        getOfficeShiftsList(payload);
      }
      getJobShiftsList(payload);
      callGetJobsList();
    }
    return () => {
      onCreateChange(false);
    };
  }, [
    createdShiftInfo,
    createIsLoading,
    isCreated,
    selectedOrganization,
    selectedDate,
    getOfficeShiftsList,
    getJobShiftsList,
    callGetJobsList,
  ]);

  useEffect(() => {
    if (
      deleteCallback &&
      !deleteIsLoading &&
      isDeleted &&
      selectedOrganization &&
      selectedOrganization.id
    ) {
      onDeleteChange(false);
      deleteToggle();
      let payload = {
        organization_id: selectedOrganization.id,
        start_date: moment(selectedDate).startOf("month").format("YYYY-MM-DD"),
        end_date: moment(selectedDate).endOf("month").format("YYYY-MM-DD"),
      };
      // Only fetch office shifts for operation type organizations
      if (orgType !== "service") {
        getOfficeShiftsList(payload);
      }
      getJobShiftsList(payload);
      callGetJobsList();
    }
  }, [
    deleteCallback,
    deleteIsLoading,
    isDeleted,
    selectedOrganization,
    selectedDate,
    getOfficeShiftsList,
    getJobShiftsList,
    callGetJobsList,
  ]);

  useEffect(() => {
    if (
      deleteJobCallback &&
      !workOrderJobDeleteIsLoading &&
      isDeleted &&
      selectedOrganization &&
      selectedOrganization.id
    ) {
      onDeleteChange(false);
      deleteToggle();
      let payload = {
        organization_id: selectedOrganization.id,
        start_date: moment(selectedDate).startOf("month").format("YYYY-MM-DD"),
        end_date: moment(selectedDate).endOf("month").format("YYYY-MM-DD"),
      };
      // Only fetch office shifts for operation type organizations
      if (orgType !== "service") {
        getOfficeShiftsList(payload);
      }
      getJobShiftsList(payload);
      callGetJobsList();
    }
  }, [
    deleteJobCallback,
    workOrderJobDeleteIsLoading,
    isDeleted,
    selectedOrganization,
    selectedDate,
    getOfficeShiftsList,
    getJobShiftsList,
    callGetJobsList,
  ]);

  useEffect(() => {
    // Close popup and refresh when edit is successful
    // Check editCallback && !editIsLoading (isEdited is optional - can be from EditShift popup or drag-and-drop)
    if (
      editCallback &&
      !editIsLoading &&
      selectedOrganization &&
      selectedOrganization.id
    ) {
      // Close the popup if it's open
      if (editIsOpen) {
        editToggle();
      }
      // Reset isEdited flag
      if (isEdited) {
        onEditChange(false);
      }
      // Refresh data in background
      let payload = {
        organization_id: selectedOrganization.id,
        start_date: moment(selectedDate).startOf("month").format("YYYY-MM-DD"),
        end_date: moment(selectedDate).endOf("month").format("YYYY-MM-DD"),
        isBackground: true, // Background refresh for optimistic updates
      };
      // Only fetch office shifts for operation type organizations
      if (orgType !== "service") {
        getOfficeShiftsList(payload);
      }
      getJobShiftsList(payload);
      callGetJobsList();
    }
    return () => {
      if (isEdited) {
        onEditChange(false);
      }
    };
  }, [
    editCallback,
    editIsLoading,
    isEdited,
    editIsOpen,
    selectedOrganization,
    selectedDate,
    getOfficeShiftsList,
    getJobShiftsList,
    callGetJobsList,
  ]);

  // Refresh lists after successful job schedule updates
  useEffect(() => {
    if (jobUpdate && selectedOrganization && selectedOrganization.id) {
      // Check if any pending operations are "unschedule" - if so, skip refresh
      // because optimistic UI already removed them from calendar
      const hasUnscheduleOp = Array.from(pendingOperations.values()).some(
        op => op.operationType === "unschedule",
      );

      if (hasUnscheduleOp) {
        // Skip the refresh for unschedule operations since optimistic UI is already correct
        setSkipNextRefresh(true);
        return;
      }

      const payload = {
        organization_id: selectedOrganization.id,
        start_date: moment(selectedDate).startOf("month").format("YYYY-MM-DD"),
        end_date: moment(selectedDate).endOf("month").format("YYYY-MM-DD"),
        isBackground: true, // Background refresh for optimistic updates
      };
      // Only fetch office shifts for operation type organizations
      if (orgType !== "service") {
        getOfficeShiftsList(payload);
      }
      getJobShiftsList(payload);
      callGetJobsList();
    }
  }, [
    jobUpdate,
    selectedOrganization,
    selectedDate,
    getOfficeShiftsList,
    getJobShiftsList,
    callGetJobsList,
    pendingOperations,
  ]);

  // Clear pending unschedule operations after API completes successfully
  useEffect(() => {
    if (skipNextRefresh && jobUpdate) {
      // Clear unschedule operations from pending since API succeeded
      setPendingOperations(prev => {
        const next = new Map(prev);
        // Remove all unschedule operations
        Array.from(next.entries()).forEach(([key, op]) => {
          if (op.operationType === "unschedule") {
            next.delete(key);
          }
        });
        return next;
      });
      // Reset the skip flag for next time
      setSkipNextRefresh(false);
    }
  }, [skipNextRefresh, jobUpdate]);

  // Error handling: revert optimistic updates on API failures
  useEffect(() => {
    // Detect edit shift failures: editIsLoading becomes false but editCallback is false
    // and we have pending operations
    if (
      !editIsLoading &&
      !editCallback &&
      pendingOperations.size > 0 &&
      isEdited
    ) {
      // Check if any pending operations are for office shifts (editShift)
      const officePendingOps = Array.from(pendingOperations.entries()).filter(
        ([, op]) => op.type === "office" && op.operationType === "reschedule",
      );

      if (officePendingOps.length > 0) {
        // Revert optimistic updates for failed operations
        setShiftsList(prev => {
          let updated = [...prev];
          officePendingOps.forEach(([eventId, op]) => {
            const idx = updated.findIndex(e => e.id === eventId);
            if (idx >= 0 && op.originalState) {
              // Revert to original state
              updated[idx] = { ...op.originalState };
            }
          });
          return updated;
        });

        // Remove failed operations from pending
        setPendingOperations(prev => {
          const next = new Map(prev);
          officePendingOps.forEach(([eventId]) => next.delete(eventId));
          return next;
        });

        createNotification(
          "error",
          "Failed to update shift. Changes have been reverted.",
          3000,
        );
        setIsEdited(false);
      }
    }
  }, [editIsLoading, editCallback, pendingOperations, isEdited]);

  // Clean up pending operations on unmount or view/date changes
  useEffect(() => {
    return () => {
      // Clear pending operations on unmount
      setPendingOperations(new Map());
    };
  }, []);

  // Clear pending operations when view or date changes significantly
  useEffect(() => {
    // Clear operations that are no longer relevant due to view/date change
    setPendingOperations(new Map());
  }, [view, selectedDate]);

  // Smart merge logic: preserve optimistic updates during background refresh
  useEffect(() => {
    if (
      (officeShiftsList && officeShiftsList.length > 0) ||
      (jobShiftsList && jobShiftsList.length > 0)
    ) {
      // Only include office shifts for operation type organizations
      const apiList = orgType === "service"
        ? [...jobShiftsList]
        : [...officeShiftsList, ...jobShiftsList];

      // If there are pending operations, merge intelligently
      if (pendingOperations.size > 0) {
        setShiftsList(prev => {
          const apiMap = new Map(apiList.map(item => [item.id, item]));
          const result = [];
          const completedOps = new Set();

          // First, add all items from API (source of truth for non-pending items)
          apiList.forEach(item => {
            const pendingOp = pendingOperations.get(item.id);
            if (!pendingOp) {
              // Normal API item, add it
              result.push(item);
              return;
            }
            // Pending operation exists for this item: only mark completed
            // when the API reflects the optimistic target. Otherwise keep optimistic.
            if (apiMatchesOptimistic(item, pendingOp)) {
              result.push(item);
              completedOps.add(item.id);
            } else {
              // Prefer optimistic version until API catches up
              result.push(pendingOp.optimisticState);
            }
          });

          // Then, add pending optimistic items that aren't in API yet
          if (Array.isArray(prev)) {
            prev.forEach(item => {
              const pendingOp = pendingOperations.get(item.id);
              if (
                pendingOp &&
                !apiMap.has(item.id) &&
                !completedOps.has(item.id)
              ) {
                // Keep optimistic version if not yet in API response
                result.push(item);
              }
            });
          }

          // Clean up completed operations
          if (completedOps.size > 0) {
            setPendingOperations(prevOps => {
              const next = new Map(prevOps);
              completedOps.forEach(id => next.delete(id));
              return next;
            });
          }

          return result;
        });
      } else {
        // No pending operations, simple replacement
        setShiftsList(apiList);
      }
    } else if (
      (!officeShiftsList || officeShiftsList.length === 0) &&
      (!jobShiftsList || jobShiftsList.length === 0)
    ) {
      // Only clear if no pending operations
      if (pendingOperations.size === 0) {
        setShiftsList([]);
      }
    }
  }, [officeShiftsList, jobShiftsList, pendingOperations, orgType]);

  const MonthDateHeader = useCallback(
    ({ date, label }) => {
      // Month view "day number" header styling (Figma-like) without affecting DnD.
      const isOffRange =
        moment(date).month() !== moment(selectedDate).month() ||
        moment(date).year() !== moment(selectedDate).year();
      const isToday = moment(date).isSame(moment(), "day");

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "100%",
            pointerEvents: "none", // keep drag/drop hover targeting consistent
          }}
        >
          <span
            style={{
              fontSize: "14px",
              fontWeight: 700,
              lineHeight: "20px",
              color: isOffRange ? "rgba(61, 77, 105, 0.3)" : "#1f2939",
              // "Today" keeps existing circle styling from CSS; this just ensures text stays correct.
              opacity: isToday ? 1 : 1,
              pointerEvents: "none",
            }}
          >
            {label}
          </span>
        </div>
      );
    },
    [selectedDate],
  );

  const eventPropGetter = useCallback(event => {
    // Figma-style event card:
    // - tinted background matching legend colors
    // NOTE: per latest request, do NOT add extra border color/stripes; job vs office is
    // already distinguished by their different colors.
    const isJob = Boolean(event?.work_order?.id);
    const isOffice = Boolean(event?.office);

    // Prioritize theme_color if provided, otherwise use defaults based on type
    const backgroundColor = event?.theme_color
      ? event.theme_color
      : isOffice
        ? "#f4ecfe" // Shift background color (matches legend)
        : isJob
          ? "#edf8fe" // Job background color (matches legend)
          : "#D2E4FE";

    return {
      ...(event && {
        style: {
          backgroundColor,
          color: "#1f2939",
          borderRadius: "4px",
          boxSizing: "border-box",
          padding: 0, // padding handled by DraggableEvent inner container
        },
      }),
    };
  }, []);

  const selectTeamMember = e => {
    // let organizationId = selectedOrganization ? selectedOrganization.id : "";
    // const startDate = moment(selectedDate)
    //   .startOf("month")
    //   .format("YYYY-MM-DD");
    // const endDate = moment(selectedDate).endOf("month").format("YYYY-MM-DD");
    // if (organizationId) {
    //   const payload = {
    //     organization_id: organizationId,
    //     start_date: startDate,
    //     end_date: endDate,
    //     user_id: e.id,
    //   };
    //   if (selectedProject && selectedProject.value) {
    //     payload.project_id = selectedProject.value;
    //   }
    //   if (selectedTask && selectedTask.value) {
    //     payload.task_id = selectedTask.value;
    //   }
    //   if (
    //     selectedTimezone &&
    //     selectedTimezone.type === "Member" &&
    //     e.timezone &&
    //     e.timezone.tz
    //   ) {
    //     payload.timezone = e.timezone.tz;
    //     setSelectedTimezone({
    //       type: "Member",
    //       timezone: e.timezone.tz,
    //       offset: e.timezone.offset,
    //     });
    //   }
    //   setSelectedMember(e);
    //   getOtherTimesheets(payload);
    // }
  };

  const clearMember = () => {
    // let organizationId = selectedOrganization ? selectedOrganization.id : "";
    // const startDate = moment(selectedDate)
    //   .startOf("month")
    //   .format("YYYY-MM-DD");
    // const endDate = moment(selectedDate).endOf("month").format("YYYY-MM-DD");
    // if (organizationId) {
    //   const payload = {
    //     organization_id: organizationId,
    //     start_date: startDate,
    //     end_date: endDate,
    //   };
    //   if (selectedProject && selectedProject.value) {
    //     payload.project_id = selectedProject.value;
    //   }
    //   if (selectedTask && selectedTask.value) {
    //     payload.task_id = selectedTask.value;
    //   }
    //   if (selectedOrganization.timezone) {
    //     setSelectedTimezone({
    //       type: "Organization",
    //       timezone: selectedOrganization.timezone,
    //       offset: selectedOrganization.timezoneoffset,
    //     });
    //   }
    //   getOtherTimesheets(payload);
    // }
    // setSelectedMember("");
  };

  const goToNext = () => {
    if (selectedOrganization && selectedOrganization.id) {
      let currentMonthStart = moment(selectedDate)
        .startOf("month")
        .format("YYYY-MM-DD");
      let currentMonthEnd = moment(selectedDate)
        .endOf("month")
        .format("YYYY-MM-DD");
      let nextMonthDate = moment(selectedDate).add(1, "M").format("YYYY-MM-DD");
      let nextMonthStart = moment(nextMonthDate)
        .startOf("month")
        .format("YYYY-MM-DD");
      let nextMonthEnd = moment(nextMonthDate)
        .endOf("month")
        .format("YYYY-MM-DD");
      let nextWeekDate = moment(selectedDate)
        .add(1, "weeks")
        .format("YYYY-MM-DD");
      let nextWeekEnd = moment(nextWeekDate).endOf("week").format("YYYY-MM-DD");
      let nextDate = moment(selectedDate).add(1, "days").format("YYYY-MM-DD");
      let payload = {
        organization_id: selectedOrganization.id,
        start_date: nextMonthStart,
        end_date: nextMonthEnd,
      };
      if (view === "month") {
        // Only fetch office shifts for operation type organizations
        if (orgType !== "service") {
          getOfficeShiftsList(payload);
        }
        getJobShiftsList(payload);
        setSelectedDate(nextMonthDate);
      } else if (view === "week") {
        if (moment(nextWeekEnd).isAfter(currentMonthEnd)) {
          // Only fetch office shifts for operation type organizations
          if (orgType !== "service") {
            getOfficeShiftsList(payload);
          }
          getJobShiftsList(payload);
        }
        setSelectedDate(nextWeekDate);
      } else if (view === "day") {
        if (moment(nextDate).isAfter(currentMonthEnd)) {
          // Only fetch office shifts for operation type organizations
          if (orgType !== "service") {
            getOfficeShiftsList(payload);
          }
          getJobShiftsList(payload);
        }
        setSelectedDate(nextDate);
      }
    }
  };
  const goToBack = () => {
    if (selectedOrganization && selectedOrganization.id) {
      let currentMonthStart = moment(selectedDate)
        .startOf("month")
        .format("YYYY-MM-DD");
      let currentMonthEnd = moment(selectedDate)
        .endOf("month")
        .format("YYYY-MM-DD");
      let prevMonthDate = moment(selectedDate)
        .subtract(1, "M")
        .format("YYYY-MM-DD");
      let prevMonthStart = moment(prevMonthDate)
        .startOf("month")
        .format("YYYY-MM-DD");
      let prevMonthEnd = moment(prevMonthDate)
        .endOf("month")
        .format("YYYY-MM-DD");
      let prevWeekDate = moment(selectedDate)
        .subtract(1, "weeks")
        .format("YYYY-MM-DD");
      let prevWeekStart = moment(prevWeekDate)
        .startOf("week")
        .format("YYYY-MM-DD");
      let prevDate = moment(selectedDate)
        .subtract(1, "days")
        .format("YYYY-MM-DD");
      let payload = {
        organization_id: selectedOrganization.id,
        start_date: prevMonthStart,
        end_date: prevMonthEnd,
      };
      if (view === "month") {
        // Only fetch office shifts for operation type organizations
        if (orgType !== "service") {
          getOfficeShiftsList(payload);
        }
        getJobShiftsList(payload);
        setSelectedDate(prevMonthDate);
      } else if (view === "week") {
        if (moment(prevWeekStart).isBefore(currentMonthStart)) {
          // Only fetch office shifts for operation type organizations
          if (orgType !== "service") {
            getOfficeShiftsList(payload);
          }
          getJobShiftsList(payload);
        }
        setSelectedDate(prevWeekDate);
      } else if (view === "day") {
        if (moment(prevDate).isBefore(currentMonthStart)) {
          // Only fetch office shifts for operation type organizations
          if (orgType !== "service") {
            getOfficeShiftsList(payload);
          }
          getJobShiftsList(payload);
        }
        setSelectedDate(prevDate);
      }
    }
  };
  const onMonthSelect = date => {
    // let organizationId = selectedOrganization ? selectedOrganization.id : "";
    // const startDate = moment(date).format("YYYY-MM-DD");
    // const endDate = moment(date).endOf("month").format("YYYY-MM-DD");
    // setSelectedDate(date);
    // if (organizationId) {
    //   const payload = {
    //     organization_id: organizationId,
    //     start_date: startDate,
    //     end_date: endDate,
    //   };
    //   if (selectedProject && selectedProject.value) {
    //     payload.project_id = selectedProject.value;
    //   }
    //   if (selectedTask && selectedTask.value) {
    //     payload.task_id = selectedTask.value;
    //   }
    //   if (selectedMember && selectedMember.id) {
    //     payload.user_id = selectedMember.id;
    //   }
    //   if (selectedTimezone && selectedTimezone.type === "Member") {
    //     payload.timezone = selectedTimezone.timezone;
    //   }
    //   getOtherTimesheets(payload);
    // }
    // setSelectedDate(moment(date).format("YYYY-MM-DD"));
  };

  const onTimezoneSelect = timezone => {
    // let organizationId = selectedOrganization ? selectedOrganization.id : "";
    // const startDate = moment(selectedDate)
    //   .startOf("month")
    //   .format("YYYY-MM-DD");
    // const endDate = moment(selectedDate).endOf("month").format("YYYY-MM-DD");
    // setSelectedTimezone(timezone);
    // if (organizationId) {
    //   const payload = {
    //     organization_id: organizationId,
    //     start_date: startDate,
    //     end_date: endDate,
    //   };
    //   if (selectedProject && selectedProject.value) {
    //     payload.project_id = selectedProject.value;
    //   }
    //   if (selectedTask && selectedTask.value) {
    //     payload.task_id = selectedTask.value;
    //   }
    //   if (selectedMember && selectedMember.id !== "") {
    //     payload.user_id = selectedMember.id;
    //   }
    //   if (timezone && timezone.type === "Member") {
    //     payload.timezone = timezone.timezone;
    //   }
    //   getOtherTimesheets(payload);
    // }
  };

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const showMoreToggle = () => {
    setShowMoreIsOpen(!showMoreIsOpen);
  };

  const createToggle = () => {
    setCreateIsOpen(!createIsOpen);
  };

  const createOptionToggle = () => {
    setCreateOptionIsOpen(!createOptionIsOpen);
  };

  const editToggle = () => {
    setEditIsOpen(!editIsOpen);
  };

  const deleteToggle = () => {
    setDeleteIsOpen(!deleteIsOpen);
  };

  const pricingPopupToggle = () => {
    setPricingPopup(!pricingPopup);
  };

  const editJobToggle = () => {
    setEditJobIsOpen(!editJobIsOpen);
  };

  const onCreateChange = value => {
    setIsCreated(value);
  };

  const onDeleteChange = value => {
    setIsDeleted(value);
  };

  const onEditChange = value => {
    setIsEdited(value);
  };

  const showDetails = event => {
    toggle();
    setSelectedShift(event);
  };

  const handleUnscheduledJobDropped = (job, date) => {
    // Open the time range modal to let user set start/end time
    setSelectedJobForTimeRange(job);
    setSelectedDateForTimeRange(date);
    setIsTimeRangeModalOpen(true);
  };

  const handleTimeRangeConfirm = ({ startTime, endTime, isAnyTimeSet, selectedDate, jobId }) => {
    if (!jobId || !selectedDate) return;

    const job = unscheduledJobsList.find(j => j.id === jobId);
    if (!job || !job.work_order?.id) {
      createNotification("error", "Job not found", 3000);
      setIsTimeRangeModalOpen(false);
      return;
    }

    const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();
    const dropDateInTz = moment.tz(selectedDate, orgTimezone);

    let payload;
    if (isAnyTimeSet) {
      // Whole day — same as regular unscheduled→month-cell drop
      payload = createSchedulePayload(job, selectedDate, selectedOrganization);
    } else {
      // Specific times chosen by user
      const startMoment = moment.tz(
        dropDateInTz.format("YYYY-MM-DD") + " " + startTime,
        "YYYY-MM-DD HH:mm",
        orgTimezone,
      );
      const endMoment = moment.tz(
        dropDateInTz.format("YYYY-MM-DD") + " " + endTime,
        "YYYY-MM-DD HH:mm",
        orgTimezone,
      );
      payload = {
        organization_id: selectedOrganization.id,
        workOrder_id: job.work_order.id,
        job_id: job.id,
        status: "scheduled",
        required_start_date: dropDateInTz.clone().startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        required_end_date: dropDateInTz.clone().endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        start_time: startMoment.format("YYYY-MM-DDTHH:mm:ss"),
        end_time: endMoment.format("YYYY-MM-DDTHH:mm:ss"),
        is_anytime: false,
      };
    }

    if (payload) {
      // Optimistic UI: remove from unscheduled, add to calendar immediately
      // start_time / end_time must be UTC ISO strings (with Z) so parseDateTimeInTimezone
      // correctly interprets them — the payload fields are local-tz strings for the API,
      // but the calendar renderer expects UTC-based strings matching what the API returns.
      const optimisticStartTime = isAnyTimeSet
        ? dropDateInTz.clone().startOf("day").toISOString()
        : moment.tz(
            dropDateInTz.format("YYYY-MM-DD") + " " + startTime,
            "YYYY-MM-DD HH:mm",
            orgTimezone,
          ).toISOString();
      const optimisticEndTime = isAnyTimeSet
        ? dropDateInTz.clone().endOf("day").toISOString()
        : moment.tz(
            dropDateInTz.format("YYYY-MM-DD") + " " + endTime,
            "YYYY-MM-DD HH:mm",
            orgTimezone,
          ).toISOString();

      const optimisticState = {
        ...job,
        start_time: optimisticStartTime,
        end_time: optimisticEndTime,
        is_anytime: isAnyTimeSet,
        status: "scheduled",
      };

      trackOptimisticUpdate(
        job.id,
        { ...job, status: "unscheduled" },
        optimisticState,
        "schedule",
        "job",
      );

      setUnscheduledJobsList(prev =>
        Array.isArray(prev) ? prev.filter(j => j.id !== jobId) : prev,
      );
      setShiftsList(prev => {
        const next = Array.isArray(prev) ? [...prev] : [];
        const idx = next.findIndex(e => e.id === job.id);
        if (idx >= 0) next[idx] = { ...next[idx], ...optimisticState };
        else next.push(optimisticState);
        return next;
      });

      updateJobRequest(payload);
      setIsEdited(true);
    } else {
      createNotification("error", "Failed to schedule job", 3000);
    }

    setIsTimeRangeModalOpen(false);
    setSelectedJobForTimeRange(null);
    setSelectedDateForTimeRange(null);
  };

  const onShowMore = useCallback(
    (events, date) => {
      setShiftsOnSelectedDate(events);
      setSelectedDateOfShowMore(date);
      showMoreToggle();
    },
    [selectedOrganization],
  );

  // Keep the "Show more" right panel list in sync with the live calendar state.
  // This ensures that after a drag/drop reschedule, the item disappears from this list.
  const showMorePanelShifts = useMemo(() => {
    if (!showMoreIsOpen) return shiftsOnSelectedDate;
    if (!selectedDateOfShowMore) return shiftsOnSelectedDate;
    if (!Array.isArray(ShiftsList)) return shiftsOnSelectedDate;

    const dayKey = moment(selectedDateOfShowMore)
      .tz(timeZone)
      .format("YYYY-MM-DD");

    const eventsForDay = ShiftsList.filter(ev => {
      const evDay = parseDateTimeInTimezone(ev?.start_time, timeZone)?.format(
        "YYYY-MM-DD",
      );
      return evDay === dayKey;
    });

    // Only overflow (hidden) events: align with month view (first 2 visible).
    const sortedEventsForDay = [...eventsForDay].sort((a, b) => {
      const aAllDay = a?.is_anytime === true ? 1 : 0;
      const bAllDay = b?.is_anytime === true ? 1 : 0;
      if (aAllDay !== bAllDay) return bAllDay - aAllDay;

      const aStart = parseDateTimeInTimezone(a?.start_time, timeZone);
      const bStart = parseDateTimeInTimezone(b?.start_time, timeZone);
      const aTs = aStart ? aStart.valueOf() : 0;
      const bTs = bStart ? bStart.valueOf() : 0;
      if (aTs !== bTs) return aTs - bTs;

      const aId = a?.id ?? 0;
      const bId = b?.id ?? 0;
      return aId > bId ? 1 : aId < bId ? -1 : 0;
    });

    return sortedEventsForDay.slice(2);
  }, [
    showMoreIsOpen,
    shiftsOnSelectedDate,
    ShiftsList,
    selectedDateOfShowMore,
    timeZone,
  ]);

  const calendarEvents = useMemo(() => {
    if (!Array.isArray(ShiftsList)) return ShiftsList;
    if (view !== Views.MONTH) return ShiftsList;

    const dayCounts = new Map();
    return ShiftsList.filter(ev => {
      const dayKey = parseDateTimeInTimezone(ev?.start_time, timeZone)?.format(
        "YYYY-MM-DD",
      );
      if (!dayKey) return true;
      const count = dayCounts.get(dayKey) || 0;
      if (count >= 2) {
        return false;
      }
      dayCounts.set(dayKey, count + 1);
      return true;
    });
  }, [ShiftsList, view, timeZone]);

  const onSelectSlot = useCallback(
    slotInfo => {
      if (
        selectedOrganization &&
        selectedOrganization.role &&
        (selectedOrganization.role === "admin" ||
          selectedOrganization.role === "owner") &&
        slotInfo &&
        slotInfo.slots &&
        slotInfo.slots[0]
      ) {
        // Check if the selected slot date is today or in the future
        const selectedSlotDate = moment.tz(slotInfo.slots[0], timeZone);
        const today = moment.tz(timeZone).startOf('day');
        const selectedDateStart = selectedSlotDate.startOf('day');

        // Only allow if selected date is today or future (not past dates)
        // Silently prevent action for past dates without showing notification
        if (selectedDateStart.isSameOrAfter(today)) {
          setSelectedStartDate(slotInfo.slots[0]);
          createOptionToggle();
        }
      }
    },
    [selectedOrganization, timeZone],
  );

  const controlJobCreate = () => {
    history.push({
      pathname: createItemPath,
      state: {
        jobStatus: "ready-to-dispatch",
        startDate: selectedStartDate && selectedStartDate,
      },
    });
  };

  const controlOfficeCreate = () => {
    // if (
    //   selectedOrganization &&
    //   selectedOrganization.id &&
    //   (plan === "elite" || (plan === "free" && officeShiftsList.length < 5))
    // ) {
    //   createToggle();
    // } else {
    //   alert("You can't create more than 5 shifts with free plan.");
    // }
    createToggle();
  };

  const onView = useCallback(newView => setView(newView), [setView]);

  // Helper function to find object by ID in officeShiftsList or jobShiftsList
  const findObjectById = useCallback(
    (id, office, jobs) => findObjectByIdHelper(id, office, jobs),
    [],
  );

  // Helper function to find job from unscheduled list
  const findJobFromUnscheduledList = useCallback(
    (id, list) => findJobFromUnscheduledListHelper(id, list),
    [],
  );

  // Helper function to determine type (job or office)
  const determineType = useCallback(object => determineTypeHelper(object), []);

  // Helper function to validate future date
  const validateFutureDate = useCallback(
    dropDate => validateFutureDateHelper(dropDate),
    [],
  );

  // Helper function to create reschedule payload (Scenario 1: Calendar → Calendar)
  const createReschedulePayload = useCallback(
    (object, dropDate, org) =>
      createReschedulePayloadHelper(object, dropDate, org),
    [],
  );

  // Helper function to create unscheduled payload (Scenario 2: Calendar → Unscheduled)
  const createUnscheduledPayload = useCallback(
    (object, org) => createUnscheduledPayloadHelper(object, org),
    [],
  );

  // Helper function to create schedule payload (Scenario 3: Unscheduled → Calendar)
  const createSchedulePayload = useCallback(
    (object, dropDate, org) =>
      createSchedulePayloadHelper(object, dropDate, org),
    [],
  );

  // Helper function to create time slot payload (for week/day view drops)
  const createTimeSlotPayload = useCallback(
    (object, slotStart, slotEnd, org) =>
      createTimeSlotPayloadHelper(object, slotStart, slotEnd, org),
    [],
  );

  const handleEventDrop = dropInfo => {
    // Scenario 1: Calendar Event → Calendar (Reschedule within calendar)
    // react-big-calendar passes an object with: { event, start, end, allDay }
    // The event object already has updated start/end times from the library
    const eventId = dropInfo.event?.id;
    if (!eventId) {
      createNotification("error", "Event not found", 3000);
      return;
    }

    // Find the full object (only search office shifts for operation type)
    const fullObject = findObjectById(
      eventId,
      orgType === "service" ? [] : officeShiftsList,
      jobShiftsList,
    );
    if (!fullObject) {
      createNotification("error", "Event not found", 3000);
      return;
    }

    // Store original event data for potential revert
    const originalEvent = {
      ...fullObject,
      start_time: fullObject.start_time,
      end_time: fullObject.end_time,
    };

    // Get new drop date from dropInfo (dropInfo.start has the new date/time)
    const newDropDate = dropInfo.start;
    if (!newDropDate) {
      createNotification("error", "Invalid date selected", 3000);
      return;
    }

    // Validate future date
    const validation = validateFutureDate(newDropDate);
    if (!validation.isValid) {
      // Show error notification
      createNotification("error", validation.error, 3000);
      // Revert event to original position in ShiftsList
      setShiftsList(prevList => {
        const updatedList = prevList.map(item =>
          item.id === eventId
            ? {
              ...item,
              start_time: originalEvent.start_time,
              end_time: originalEvent.end_time,
            }
            : item,
        );
        return updatedList;
      });
      return;
    }

    // Determine type
    const type = determineType(fullObject);

    // Create payload based on is_anytime value
    const payload = createReschedulePayload(
      fullObject,
      newDropDate,
      selectedOrganization,
    );

    if (payload) {
      // Calculate new times preserving original time components for is_anytime: false
      const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();
      let newStartTime, newEndTime;

      if (
        fullObject.is_anytime === false &&
        fullObject.start_time &&
        fullObject.end_time
      ) {
        // Preserve original time components (hours, minutes, seconds)
        const existingStartTime = parseDateTimeInTimezone(
          fullObject.start_time,
          orgTimezone,
        );
        const existingEndTime = parseDateTimeInTimezone(
          fullObject.end_time,
          orgTimezone,
        );

        if (existingStartTime && existingEndTime) {
          // Extract time components
          const startHour = existingStartTime.hour();
          const startMinute = existingStartTime.minute();
          const startSecond = existingStartTime.second();
          const endHour = existingEndTime.hour();
          const endMinute = existingEndTime.minute();
          const endSecond = existingEndTime.second();

          // Apply to new date from dropInfo
          const newDropDateInTz = moment.tz(newDropDate, orgTimezone);
          newStartTime = moment
            .tz(
              newDropDateInTz.format("YYYY-MM-DD") +
              ` ${String(startHour).padStart(2, "0")}:${String(
                startMinute,
              ).padStart(2, "0")}:${String(startSecond).padStart(2, "0")}`,
              "YYYY-MM-DD HH:mm:ss",
              orgTimezone,
            )
            .format();
          newEndTime = moment
            .tz(
              newDropDateInTz.format("YYYY-MM-DD") +
              ` ${String(endHour).padStart(2, "0")}:${String(
                endMinute,
              ).padStart(2, "0")}:${String(endSecond).padStart(2, "0")}`,
              "YYYY-MM-DD HH:mm:ss",
              orgTimezone,
            )
            .format();
        } else {
          // Fallback to dropInfo times if parsing fails
          newStartTime = moment(dropInfo.start).format();
          newEndTime = dropInfo.end
            ? moment(dropInfo.end).format()
            : moment(dropInfo.start).endOf("day").format();
        }
      } else {
        // For is_anytime: true, use dropInfo times (whole day)
        newStartTime = moment(dropInfo.start).startOf("day").format();
        newEndTime = dropInfo.end
          ? moment(dropInfo.end).endOf("day").format()
          : moment(dropInfo.start).endOf("day").format();
      }

      // Track the drag operation for success/failure handling
      const newEvent = {
        ...fullObject,
        start_time: newStartTime,
        end_time: newEndTime,
      };

      setPendingDragOperation({
        eventId: fullObject.id,
        originalEvent,
        newEvent,
        type,
      });

      // Call API for office shifts - data will be refreshed from API after successful update
      if (type === "office" && editShift) {
        editShift(payload);
        setIsEdited(true);
      }
      // Call API for jobs
      else if (type === "job" && updateJobRequest) {
        // Add status to payload (keep as scheduled when rescheduling)
        const jobPayload = {
          ...payload,
          status: "scheduled",
        };
        updateJobRequest(jobPayload);
        setIsEdited(true);
      }
    } else {
      createNotification("error", "Failed to reschedule event", 3000);
      // Revert event to original position if payload creation fails
      setShiftsList(prevList => {
        const updatedList = prevList.map(item =>
          item.id === eventId
            ? {
              ...item,
              start_time: originalEvent.start_time,
              end_time: originalEvent.end_time,
            }
            : item,
        );
        return updatedList;
      });
      // Clear pending operation if payload creation fails
      setPendingDragOperation(null);
    }
  };

  return (
    <div className="content" style={{ minHeight: "100vh" }}>
      <ShiftDetailsPopup
        selectedOrganization={selectedOrganization}
        isOpen={isOpen}
        toggle={toggle}
        shift={selectedShift}
        deleteToggle={deleteToggle}
        setSelectedJob={setSelectedJob}
        editToggle={editToggle}
        editJobToggle={editJobToggle}
        history={history}
        orgMembersShortList={orgMembersShortList}
        memberShortListIsLoading={memberShortListIsLoading}
        getOrganizationMembersShortList={getOrganizationMembersShortList}
        editShift={editShift}
        updateJobRequest={updateJobRequest}
      />
      <CreateOptionPopup
        selectedOrganization={selectedOrganization}
        isOpen={createOptionIsOpen}
        toggle={createOptionToggle}
        createToggle={createToggle}
        history={history}
        selectedStartDate={selectedStartDate}
        controlJobCreate={controlJobCreate}
        controlOfficeCreate={controlOfficeCreate}
        plan={
          selectedOrganization &&
          selectedOrganization.plan &&
          selectedOrganization.plan.name
        }
      />
      <CreateShift
        isOpen={createIsOpen}
        toggle={createToggle}
        selectedStartDate={selectedStartDate}
        onCreateChange={onCreateChange}
        pricingPopupToggle={pricingPopupToggle}
        setPricingPopupText={setPricingPopupText}
      />
      <EditShift
        isOpen={editIsOpen}
        toggle={editToggle}
        shift={selectedShift}
        onEditChange={onEditChange}
      />
      <EditJobPopup
        isOpen={editJobIsOpen}
        toggle={editJobToggle}
        selectedJob={selectedJob}
        setSelectedJob={setSelectedJob}
        pricingPopupToggle={pricingPopupToggle}
        setPricingPopup={setPricingPopup}
      />
      <PricingPlan
        isOpen={pricingPopup}
        toggle={pricingPopupToggle}
        popupText={pricingPopupText}
        popupTitle="Upgrade to Standard or Elite Plan to use this feature."
        history={history}
      />
      <AddJobTimeRangeModal
        isOpen={isTimeRangeModalOpen}
        toggle={() => setIsTimeRangeModalOpen(false)}
        onConfirm={handleTimeRangeConfirm}
        selectedDate={selectedDateForTimeRange}
        selectedJob={selectedJobForTimeRange}
      />
      {selectedOrganization && selectedOrganization.id && (
        <DeleteShiftPopup
          isOpen={deleteIsOpen}
          toggle={deleteToggle}
          shift={selectedShift}
          organization_id={selectedOrganization.id}
          deleteShifts={deleteShifts}
          deleteCallback={deleteCallback}
          deleteIsLoading={deleteIsLoading}
          onDeleteChange={onDeleteChange}
          isDeleted={isDeleted}
          deleteWorkOrderJob={deleteWorkOrderJob}
          workOrderJobDeleteIsLoading={workOrderJobDeleteIsLoading}
          selectedOrganization={selectedOrganization}
        />
      )}
      
      <Container
        padding="20px"
        borderRadius="16px 16px 0 0"
        marginTop="32px"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: "16px",
        }}
      >
        {appOnboardingIsFeaturesLoading ? null : (!appOnboardingFeatures?.has_schedule && !appOnboardingFeatures?.dismissed_videos?.includes("schedule") && (role === "owner" || role === "admin") ? (
          <AppOnboardingIndividualPage
            history={history}
            selectedOrganization={selectedOrganization}
            page="schedule"
            appOnboardingState={appOnboardingState}
            dismissOnboardingFeature={dismissOnboardingFeature}
            dismissFeatureLoading={dismissFeatureLoading}
          />
        ) : (
          <>
        <CommonGrid alignItem="center">
          {/* <TimesheetFilter
          page="monthly"
          selectedMember={selectedMember}
          selectMember={selectTeamMember}
          clearMember={clearMember}
          selectTimezone={onTimezoneSelect}
          selectedTimezone={selectedTimezone}
        /> */}
          <PageTitle color="#06100F" fontWeight="700" lineHeight="150%">
            Shift & Schedule
          </PageTitle>
          {selectedOrganization &&
            selectedOrganization.role &&
            (selectedOrganization.role === "admin" ||
              selectedOrganization.role === "owner") ? (
            <CommonGrid gap="8px">
              {orgType !== "service" && (
                <PrimaryWhiteBgButton
                  onClick={() => createToggle()}
                >
                  Schedule for team
                </PrimaryWhiteBgButton>
              )}
              {selectedOrganization.plan &&
                selectedOrganization.plan.name !== "standard-free" &&
                (selectedOrganization.role === "admin" ||
                  selectedOrganization.role === "owner") ? (
                <PrimaryBgButton
                  onClick={() =>
                    history.push({
                      pathname: createItemPath,
                      state: {
                        jobStatus: "ready-to-dispatch",
                        startDate: selectedStartDate && selectedStartDate,
                      },
                    })
                  }
                >
                  {createItemScheduleLabel}
                </PrimaryBgButton>
              ) : (
                <></>
              )}
            </CommonGrid>
          ) : null}
        </CommonGrid>
        <ScheduleContainer padding="0">
          {isLoading && !isBackgroundRefreshing ? (
            <ComponentCircleLoader />
          ) : (
            <DndProvider backend={HTML5Backend}>
              <DndBodyClassOnDrag />
              <CommonFlex direction="column" gap="16px">
                <ShowMoreRightSidePanel
                  selectedOrganization={selectedOrganization}
                  isOpen={showMoreIsOpen}
                  toggle={showMoreToggle}
                  shifts={showMorePanelShifts}
                  selectedDate={selectedDateOfShowMore}
                  width="290px"
                  onSelectShift={shift => {
                    setSelectedShift(shift);
                    setIsOpen(true);
                  }}
                  onCloseUnscheduled={() => setIsUnscheduledPanelOpen(false)}
                  onEnsureUnscheduledExpanded={() =>
                    setIsUnscheduledPanelOpen(true)
                  }
                />
                <ShiftScheduleToolbar
                  goToNext={goToNext}
                  goToBack={goToBack}
                  view={view}
                  onViewChange={onView}
                  selectedDate={selectedDate}
                  onMapViewClick={() => {
                    // Build query parameters for map view
                    const dateParam = moment(selectedDate).format("YYYY-MM-DD");
                    let queryString = `?date=${dateParam}`;

                    if (selectedMember && selectedMember.id) {
                      queryString += `&member=${encodeURIComponent(JSON.stringify(selectedMember))}`;
                    }

                    // Navigate to appropriate map route based on organization type
                    const mapRoute = isOperationType ? "/user/tasks-in-map" : "/user/jobs-in-map";
                    history.push({
                      pathname: mapRoute,
                      search: queryString,
                    });
                  }}
                />
                <CommonGrid
                  style={{ width: "100%", marginTop: "0" }}
                  justifyContent="flex-start"
                  columns={
                    isUnscheduledPanelOpen
                      ? `1fr ${UNSCHEDULED_EXPANDED_WIDTH}`
                      : `1fr ${UNSCHEDULED_COLLAPSED_WIDTH}`
                  }
                >
                  <CalendarContainer view={view}>
                    {view === "week" ? (
                      <WeeklyGridView
                        selectedDate={selectedDate}
                        selectedOrganization={selectedOrganization}
                        calendarStep={60}
                        events={Array.isArray(ShiftsList) ? ShiftsList : []}
                        unscheduledJobsList={unscheduledJobsList}
                        findJobFromUnscheduledList={findJobFromUnscheduledList}
                        findObjectById={findObjectById}
                        validateFutureDate={validateFutureDate}
                        createTimeSlotPayload={createTimeSlotPayload}
                        determineType={determineType}
                        updateJobRequest={updateJobRequest}
                        editShift={editShift}
                        officeShiftsList={orgType === "service" ? [] : officeShiftsList}
                        jobShiftsList={jobShiftsList}
                        setShiftsList={setShiftsList}
                        setUnscheduledJobsList={setUnscheduledJobsList}
                        setIsEdited={setIsEdited}
                        trackOptimisticUpdate={trackOptimisticUpdate}
                        onSelectEvent={shift => showDetails(shift)}
                      />
                    ) : view === "day" ? (
                      <DailyGridView
                        selectedDate={selectedDate}
                        selectedOrganization={selectedOrganization}
                        calendarStep={60}
                        events={Array.isArray(ShiftsList) ? ShiftsList : []}
                        unscheduledJobsList={unscheduledJobsList}
                        findJobFromUnscheduledList={findJobFromUnscheduledList}
                        findObjectById={findObjectById}
                        validateFutureDate={validateFutureDate}
                        createTimeSlotPayload={createTimeSlotPayload}
                        determineType={determineType}
                        updateJobRequest={updateJobRequest}
                        editShift={editShift}
                        officeShiftsList={orgType === "service" ? [] : officeShiftsList}
                        jobShiftsList={jobShiftsList}
                        setShiftsList={setShiftsList}
                        setUnscheduledJobsList={setUnscheduledJobsList}
                        setIsEdited={setIsEdited}
                        trackOptimisticUpdate={trackOptimisticUpdate}
                        onSelectEvent={shift => showDetails(shift)}
                        onUnscheduledJobDropped={handleUnscheduledJobDropped}
                      />
                    ) : (
                      <Calendar
                        className={`schedule-calendar schedule-calendar--${view}`}
                        style={{ height: "100%" }}
                        localizer={localizer}
                        startAccessor={event =>
                          parseDateTimeInTimezone(
                            event.start_time,
                            timeZone,
                          )?.toDate() || new Date()
                        }
                        endAccessor={event =>
                          parseDateTimeInTimezone(
                            event.end_time,
                            timeZone,
                          )?.toDate() || new Date()
                        }
                        titleAccessor={event =>
                          // Keep `titleAccessor` as "name only"; month view shows time via `DraggableEvent`.
                          event?.name
                            ? view === "month"
                              ? width > 1550
                                ? event.name.length > 28
                                  ? `${event.name.slice(0, 27)}...`
                                  : event.name
                                : event.name.length > 20
                                  ? `${event.name.slice(0, 19)}...`
                                  : event.name
                              : event.name
                            : event?.title || ""
                        }
                        events={calendarEvents}
                        date={moment(selectedDate).toDate()}
                        onNavigate={() => { }}
                        views={["month", "day"]}
                        drilldownView="null"
                        culture="en-GB"
                        formats={{ dateFormat: "D" }}
                        selectable
                        onSelectEvent={event => showDetails(event)}
                        step={30}
                        components={{
                          toolbar: () => null,
                          month: {
                            dateHeader: MonthDateHeader,
                          },
                          eventWrapper: props => (
                            <DraggableEventWrapper
                              {...props}
                              selectedOrganization={selectedOrganization}
                              timeZone={timeZone}
                            />
                          ),
                          event: props => (
                            <DraggableEvent
                              {...props}
                              view={view}
                              timeZone={timeZone}
                            />
                          ),
                          timeColumnWrapper: props => (
                            <TimeColumnWrapperComponent {...props} />
                          ),
                          dateCellWrapper: props => (
                            <DateCellWrapperComponent
                              {...props}
                              view={view}
                              timeZone={timeZone}
                              allEvents={ShiftsList}
                              onShowMore={onShowMore}
                              unscheduledJobsList={unscheduledJobsList}
                              selectedOrganization={selectedOrganization}
                              findJobFromUnscheduledList={
                                findJobFromUnscheduledList
                              }
                              findObjectById={findObjectById}
                              validateFutureDate={validateFutureDate}
                              createSchedulePayload={createSchedulePayload}
                              createReschedulePayload={createReschedulePayload}
                              determineType={determineType}
                              updateJobRequest={updateJobRequest}
                              editShift={editShift}
                              officeShiftsList={orgType === "service" ? [] : officeShiftsList}
                              jobShiftsList={jobShiftsList}
                              setShiftsList={setShiftsList}
                              setUnscheduledJobsList={setUnscheduledJobsList}
                              setIsEdited={setIsEdited}
                              trackOptimisticUpdate={trackOptimisticUpdate}
                              onUnscheduledJobDropped={handleUnscheduledJobDropped}
                            />
                          ),
                          timeSlotWrapper: props => (
                            <TimeSlotWrapperComponent
                              {...props}
                              unscheduledJobsList={unscheduledJobsList}
                              selectedOrganization={selectedOrganization}
                              findJobFromUnscheduledList={
                                findJobFromUnscheduledList
                              }
                              findObjectById={findObjectById}
                              validateFutureDate={validateFutureDate}
                              createTimeSlotPayload={createTimeSlotPayload}
                              determineType={determineType}
                              updateJobRequest={updateJobRequest}
                              editShift={editShift}
                              officeShiftsList={orgType === "service" ? [] : officeShiftsList}
                              jobShiftsList={jobShiftsList}
                              setShiftsList={setShiftsList}
                              setUnscheduledJobsList={setUnscheduledJobsList}
                              setIsEdited={setIsEdited}
                              trackOptimisticUpdate={trackOptimisticUpdate}
                              calendarStep={30}
                              view={view}
                              selectedDate={selectedDate}
                            />
                          ),
                        }}
                        tooltipAccessor={() => `click to view details`}
                        eventPropGetter={eventPropGetter}
                        onShowMore={onShowMore}
                        onSelectSlot={onSelectSlot}
                        view={view}
                        onView={onView}
                      />
                    )}
                  </CalendarContainer>
                  {isUnscheduledPanelOpen ? (
                    <ScheduleRightContainer
                      marginTop="0px"
                      style={{
                        width: "100%",
                        height: view === "month" ? "740px" : "555px",
                        borderRadius: "8px",
                        border: "1px solid #b4c6f9",
                      }}
                    >
                      <ScheduleRightContainerHeader
                        style={{
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "0 8px",
                          height: "40px",
                          borderTopLeftRadius: "8px",
                          borderTopRightRadius: "8px",
                          background: "#b4c6f9",
                          borderBottom: "none",
                          cursor: "pointer",
                        }}
                        onClick={() => setIsUnscheduledPanelOpen(false)}
                      >
                        <span
                          style={{
                            fontFamily: '"Mona Sans", sans-serif',
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#000e2a",
                            lineHeight: "19.74px",
                          }}
                        >
                          Unscheduled
                        </span>
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            setIsUnscheduledPanelOpen(false);
                          }}
                          style={{
                            width: "19px",
                            height: "19px",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: 0,
                            padding: 0,
                          }}
                          aria-label="Collapse unscheduled panel"
                        >
                          <UnscheduledToggleIcon src={RightArrow} alt="" />
                        </button>
                      </ScheduleRightContainerHeader>
                      <UnscheduledDropZone
                        ShiftsList={ShiftsList}
                        selectedOrganization={selectedOrganization}
                        findObjectById={findObjectById}
                        officeShiftsList={orgType === "service" ? [] : officeShiftsList}
                        jobShiftsList={jobShiftsList}
                        createUnscheduledPayload={createUnscheduledPayload}
                        determineType={determineType}
                        updateJobRequest={updateJobRequest}
                        setShiftsList={setShiftsList}
                        setUnscheduledJobsList={setUnscheduledJobsList}
                        setIsEdited={setIsEdited}
                        trackOptimisticUpdate={trackOptimisticUpdate}
                        style={{
                          height: "calc(740px - 44px)",
                          borderBottomLeftRadius: "8px",
                          borderBottomRightRadius: "8px",
                          background: "#FFFFFF",
                          padding: "8px",
                        }}
                        view={view}
                      >
                        {unscheduledJobsList.length > 0 ? (
                          unscheduledJobsList.map(job => (
                            <JobItem key={job.id} job={job} />
                          ))
                        ) : (
                          <CommonText
                            style={{ padding: "10px", margin: "0" }}
                            textAlign="center"
                          >
                            No unscheduled {isOperationType ? "jobs" : "tasks"}{" "}
                            found
                          </CommonText>
                        )}
                      </UnscheduledDropZone>
                    </ScheduleRightContainer>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "stretch",
                        paddingTop: "0px",
                      }}
                    >
                      <UnscheduledCollapsedTab
                        view={view}
                        onClick={() => setIsUnscheduledPanelOpen(true)}
                        type="button"
                      >
                        <UnscheduledToggleIcon src={LeftArrow} alt="" />
                        <UnscheduledCollapsedText>
                          Unscheduled
                        </UnscheduledCollapsedText>
                        <UnscheduledCountBadge>
                          {unscheduledJobsList.length}
                        </UnscheduledCountBadge>
                      </UnscheduledCollapsedTab>
                    </div>
                  )}
                </CommonGrid>
                <ShiftScheduleLegend
                  selectedOrganization={selectedOrganization}
                />
              </CommonFlex>
            </DndProvider>
          )}
        </ScheduleContainer>
          </>
        ))}
      </Container>
    </div>
  );
};

export default ShiftSchedule;
